import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Check, Globe, Tag, Clock, CreditCard, Users, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdmin } from '@/contexts/AdminContext';
import { cn } from '@/lib/utils';

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const SITES = [
  { id: 'shihab_romeed', name: 'Shihab Romeed (SR)' },
  { id: 'specialist_dental_london', name: 'Specialist Dental London (SDL)' },
  { id: 'london_dental_academy', name: 'London Dental Academy (LDA)' }
];

export default function ServiceForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { apiFetch } = useAdmin();
  
  const [form, setForm] = useState({ 
    siteKeys: ['shihab_romeed'],
    name: '', slug: '', shortDescription: '', description: '', 
    duration: 30, depositAmount: 50, currency: 'gbp', audience: 'patient', 
    category: '', displayOrder: 0, isActive: true, 
    requiresConsultation: false, providerIds: [] 
  });
  
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Fetch Providers based on selected sites
  useEffect(() => { 
    // If no sites selected, clear providers
    if (form.siteKeys.length === 0) {
      setProviders([]);
      return;
    }
    // Fetch providers for the first selected site for simplicity, or ideally fetch for all and merge
    apiFetch(`/providers?siteKey=${form.siteKeys[0]}`)
      .then(r => r.json())
      .then(d => setProviders(d.providers || []))
      .catch(() => {}); 
  }, [apiFetch, form.siteKeys]);

  // Fetch Categories based on selected sites
  useEffect(() => { 
    if (form.siteKeys.length === 0) {
      setCategories([]);
      return;
    }
    apiFetch(`/categories?siteKey=${form.siteKeys[0]}`)
      .then(r => r.json())
      .then(d => setCategories(d.categories || []))
      .catch(() => {}); 
  }, [apiFetch, form.siteKeys]);

  // Load existing service if editing
  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    apiFetch(`/services/${id}`).then(r => r.json()).then(d => {
      const s = d.service || d;
      setForm({
        siteKeys: s.siteKeys || (s.siteKey ? [s.siteKey] : ['shihab_romeed']),
        name: s.name || '',
        slug: s.slug || '',
        shortDescription: s.shortDescription || '',
        description: s.description || '',
        duration: s.duration || 30,
        depositAmount: s.depositAmount || 0,
        currency: s.currency || 'gbp',
        audience: s.audience || 'patient',
        category: s.category || '',
        displayOrder: s.displayOrder || 0,
        isActive: s.isActive !== false,
        requiresConsultation: s.requiresConsultation || false,
        providerIds: s.providers?.map(p => p.id) || s.providerIds || []
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id, isEdit, apiFetch]);

  const update = (f, v) => setForm(p => { 
    const n = { ...p, [f]: v }; 
    if (f === 'name' && !isEdit) n.slug = slugify(v); 
    return n; 
  });

  const toggleProvider = (pid) => setForm(p => ({
    ...p, 
    providerIds: p.providerIds.includes(pid) ? p.providerIds.filter(i => i !== pid) : [...p.providerIds, pid]
  }));

  const toggleSite = (sid) => setForm(p => {
    // We allow changing sites, but require at least 1
    const keys = p.siteKeys.includes(sid) ? p.siteKeys.filter(i => i !== sid) : [...p.siteKeys, sid];
    return { ...p, siteKeys: keys.length > 0 ? keys : p.siteKeys };
  });

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = isEdit 
        ? await apiFetch(`/services/${id}`, { method: 'PUT', body: form }) 
        : await apiFetch('/services', { method: 'POST', body: form });
        
      if (res.ok) { 
        setSaved(true); 
        setTimeout(() => navigate('/admin/services'), 1000); 
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.message || data.error || `Error ${res.status}`);
      }
    } catch(e) { 
      console.error(e); 
      setError('Network error — check connection'); 
    } finally { 
      setSaving(false); 
    }
  };

  if (loading) return (
    <div className="max-w-5xl space-y-8 animate-pulse">
      <Skeleton className="h-8 w-64" />
      <div className="grid lg:grid-cols-[1fr_400px] gap-8">
        <Skeleton className="h-[600px] rounded-2xl" />
        <Skeleton className="h-[400px] rounded-2xl" />
      </div>
    </div>
  );

  return (
    <div className="max-w-5xl pb-20">
      <div className="flex items-center gap-4 mb-8">
        <Link to="/admin/services" className="h-10 w-10 rounded-full border border-stone-200 bg-white flex items-center justify-center hover:bg-stone-50 transition-colors shadow-sm">
          <ArrowLeft className="w-4 h-4 text-stone-600" />
        </Link>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-400">Services</p>
          <h2 className="text-2xl md:text-3xl font-playfair font-medium text-stone-900 tracking-tight">
            {isEdit ? 'Edit Service' : 'Create New Service'}
          </h2>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/admin/services')} className="h-10 bg-white border-stone-200">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !form.name || form.siteKeys.length === 0} className="h-10 gap-2 bg-stone-900 hover:bg-stone-800 text-white px-6 shadow-md shadow-stone-900/10">
            {saving ? 'Saving...' : saved ? <><Check className="w-4 h-4"/> Saved</> : <><Save className="w-4 h-4"/> {isEdit ? 'Save Changes' : 'Create Service'}</>}
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-8 flex items-center gap-3 px-5 py-4 rounded-xl bg-red-50 border border-red-100 text-red-800 text-sm shadow-sm">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-100 text-red-600 shrink-0 font-bold">!</span>
          <p className="flex-1">{error}</p>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 text-xs font-medium uppercase tracking-wider">Dismiss</button>
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        
        {/* Left Column: Main Details */}
        <div className="space-y-6">
          <section className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-stone-100 rounded-lg"><Tag className="w-4 h-4 text-stone-600" /></div>
              <h3 className="text-lg font-medium text-stone-900">Basic Details</h3>
            </div>
            
            <div className="space-y-5">
              <div>
                <Label className="text-xs font-medium text-stone-500 mb-1.5 block">Service Name *</Label>
                <Input value={form.name} onChange={e => update('name', e.target.value)} className="h-11 text-base bg-stone-50/50" placeholder="e.g. Implant Consultation" />
              </div>
              
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-xs font-medium text-stone-500 mb-1.5 block">URL Slug</Label>
                  <Input value={form.slug} onChange={e => update('slug', e.target.value)} className="h-11 font-mono text-sm bg-stone-50/50" placeholder="implant-consultation" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-stone-500 mb-1.5 block">Category</Label>
                  <select 
                    value={form.category} 
                    onChange={e => update('category', e.target.value)} 
                    className="h-11 w-full rounded-md border border-stone-200 bg-stone-50/50 px-3 text-sm focus:ring-2 focus:ring-stone-900 focus:border-transparent outline-none transition-all"
                  >
                    <option value="">No Category</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-xs font-medium text-stone-500 mb-1.5 block">Short Description</Label>
                <Input value={form.shortDescription} onChange={e => update('shortDescription', e.target.value)} className="h-11 bg-stone-50/50" placeholder="A brief summary for the booking list..." />
              </div>

              <div>
                <Label className="text-xs font-medium text-stone-500 mb-1.5 block">Full Description</Label>
                <Textarea value={form.description} onChange={e => update('description', e.target.value)} rows={5} className="resize-y bg-stone-50/50 text-sm leading-relaxed" placeholder="Detailed explanation of the service, what's included, and any preparation needed..." />
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-stone-100 rounded-lg"><Users className="w-4 h-4 text-stone-600" /></div>
              <h3 className="text-lg font-medium text-stone-900">Provider Assignment</h3>
            </div>

            {providers.length === 0 ? (
              <p className="text-sm text-stone-500 bg-stone-50 p-4 rounded-xl border border-stone-100 text-center">
                Select a site first to see available providers.
              </p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {providers.map(p => {
                  const isSelected = form.providerIds.includes(p.id);
                  return (
                    <button 
                      key={p.id} 
                      onClick={() => toggleProvider(p.id)} 
                      className={cn(
                        'flex items-center gap-3 p-3 rounded-xl border text-left transition-all',
                        isSelected ? 'border-stone-900 bg-stone-900 text-white shadow-md shadow-stone-900/10' : 'border-stone-200 bg-white hover:border-stone-300 text-stone-700'
                      )}
                    >
                      <div className={cn('h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0', isSelected ? 'bg-white/20' : 'bg-stone-100 text-stone-600')}>
                        {(p.name || 'P').charAt(0)}
                      </div>
                      <div className="overflow-hidden">
                        <span className="block truncate font-medium text-sm">{p.name}</span>
                        {p.title && <span className={cn("block truncate text-[10px] mt-0.5", isSelected ? "text-stone-300" : "text-stone-400")}>{p.title}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        {/* Right Column: Settings & Config */}
        <div className="space-y-6">
          <section className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-stone-100 rounded-lg"><Globe className="w-4 h-4 text-stone-600" /></div>
              <h3 className="text-base font-medium text-stone-900">Publishing</h3>
            </div>

            <div className="space-y-5">
              <div>
                <Label className="text-xs font-medium text-stone-500 mb-2 block">Site Assignment *</Label>
                <div className="space-y-2">
                  {SITES.map(s => {
                    const isSelected = form.siteKeys.includes(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => toggleSite(s.id)}
                        className={cn(
                          "w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all",
                          isSelected 
                            ? "border-stone-900 bg-stone-900 text-white shadow-sm" 
                            : "border-stone-200 bg-white hover:border-stone-300 text-stone-700"
                        )}
                      >
                        <div className={cn(
                          "w-4 h-4 rounded border flex items-center justify-center shrink-0",
                          isSelected ? "bg-white border-white" : "border-stone-300"
                        )}>
                          {isSelected && <Check className="w-3 h-3 text-stone-900" />}
                        </div>
                        <span className="text-sm font-medium">{s.name}</span>
                      </button>
                    );
                  })}
                </div>
                {isEdit && <p className="text-[10px] text-amber-600 mt-2">Warning: Removing a site will not delete past bookings, but the service will no longer be available for new bookings on that portal.</p>}
              </div>

              <div>
                <Label className="text-xs font-medium text-stone-500 mb-1.5 block">Audience</Label>
                <select 
                  value={form.audience} 
                  onChange={e => update('audience', e.target.value)} 
                  className="h-11 w-full rounded-md border border-stone-200 bg-stone-50/50 px-3 text-sm focus:ring-2 focus:ring-stone-900 outline-none transition-all"
                >
                  <option value="patient">Patients (Public)</option>
                  <option value="professional">Professionals / Delegates</option>
                </select>
              </div>

              <div className="pt-4 border-t border-stone-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-stone-900">Active Status</p>
                    <p className="text-xs text-stone-500 mt-0.5">Visible on the booking portal</p>
                  </div>
                  <Switch checked={form.isActive} onCheckedChange={v => update('isActive', v)} />
                </div>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-stone-100 rounded-lg"><Clock className="w-4 h-4 text-stone-600" /></div>
              <h3 className="text-base font-medium text-stone-900">Duration & Ordering</h3>
            </div>

            <div className="space-y-5">
              <div>
                <Label className="text-xs font-medium text-stone-500 mb-1.5 block">Duration (Minutes)</Label>
                <div className="relative">
                  <Input type="number" min="0" step="5" value={form.duration} onChange={e => update('duration', parseInt(e.target.value) || 0)} className="h-11 bg-stone-50/50 pl-10" />
                  <Clock className="w-4 h-4 text-stone-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                </div>
              </div>
              
              <div>
                <Label className="text-xs font-medium text-stone-500 mb-1.5 block">Display Order</Label>
                <Input type="number" value={form.displayOrder} onChange={e => update('displayOrder', parseInt(e.target.value) || 0)} className="h-11 bg-stone-50/50" placeholder="0 = Highest priority" />
                <p className="text-[10px] text-stone-400 mt-1.5">Lower numbers appear first in the list.</p>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-stone-100 rounded-lg"><CreditCard className="w-4 h-4 text-stone-600" /></div>
              <h3 className="text-base font-medium text-stone-900">Pricing & Rules</h3>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium text-stone-500 mb-1.5 block">Deposit Amount</Label>
                  <Input type="number" min="0" value={form.depositAmount} onChange={e => update('depositAmount', parseFloat(e.target.value) || 0)} className="h-11 bg-stone-50/50" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-stone-500 mb-1.5 block">Currency</Label>
                  <select 
                    value={form.currency} 
                    onChange={e => update('currency', e.target.value)} 
                    className="h-11 w-full rounded-md border border-stone-200 bg-stone-50/50 px-3 text-sm focus:ring-2 focus:ring-stone-900 outline-none transition-all"
                  >
                    <option value="gbp">GBP (£)</option>
                    <option value="usd">USD ($)</option>
                    <option value="eur">EUR (€)</option>
                  </select>
                </div>
              </div>

              <div className="pt-4 border-t border-stone-100">
                <div className="flex items-center justify-between">
                  <div className="pr-4">
                    <p className="text-sm font-medium text-stone-900">Requires Consultation</p>
                    <p className="text-xs text-stone-500 mt-0.5">Patient must have consulted first</p>
                  </div>
                  <Switch checked={form.requiresConsultation} onCheckedChange={v => update('requiresConsultation', v)} />
                </div>
              </div>
            </div>
          </section>
        </div>

      </div>
    </div>
  );
}
